import { useCallback, useEffect, useRef } from 'react';
import { message } from 'antd';
import ReCAPTCHA from 'react-google-recaptcha';

export const RECAPTCHA_HEADER = 'x-recaptcha';

const RECAPTCHA_TIMEOUT_MS = 2 * 60 * 1000;
const CHALLENGE_CLOSE_GRACE_MS = 1000;

const CANCELED = Symbol('recaptcha-canceled');
const SILENT = Symbol('recaptcha-silent');

const watchChallengeClose = (onClose: () => void) => {
    const watched = new WeakSet<HTMLElement>();
    const wasVisible = new WeakSet<HTMLElement>();

    const check = () => {
        document
            .querySelectorAll<HTMLIFrameElement>(
                'iframe[src*="recaptcha/api2/bframe"], iframe[src*="recaptcha/enterprise/bframe"]',
            )
            .forEach((iframe) => {
                const container = iframe.closest<HTMLElement>('body > div');
                if (!container) return;
                if (!watched.has(container)) {
                    watched.add(container);
                    observer.observe(container, {
                        attributes: true,
                        attributeFilter: ['style'],
                    });
                }
                if (container.style.visibility !== 'hidden') {
                    wasVisible.add(container);
                } else if (wasVisible.has(container)) {
                    onClose();
                }
            });
    };

    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true });
    check();
    return () => observer.disconnect();
};

type Pending = {
    resolve: (reason: symbol) => void;
    cleanup: () => void;
};

export const useRecaptcha = () => {
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const pendingRef = useRef<Pending | null>(null);

    useEffect(
        () => () => {
            pendingRef.current?.cleanup();
            pendingRef.current = null;
        },
        [],
    );

    const cancelRecaptcha = useCallback(() => {
        pendingRef.current?.resolve(SILENT);
    }, []);

    const getRecaptchaToken = useCallback(async (): Promise<string | null> => {
        pendingRef.current?.resolve(SILENT);

        let stopWatching: (() => void) | undefined;
        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        let graceId: ReturnType<typeof setTimeout> | undefined;
        const cleanup = () => {
            stopWatching?.();
            clearTimeout(timeoutId);
            clearTimeout(graceId);
        };

        let pending: Pending | undefined;
        const interrupted = new Promise<symbol>((resolve) => {
            pending = { resolve, cleanup };
            timeoutId = setTimeout(
                () => resolve(CANCELED),
                RECAPTCHA_TIMEOUT_MS,
            );
            stopWatching = watchChallengeClose(() => {
                if (graceId) return;
                graceId = setTimeout(
                    () => resolve(CANCELED),
                    CHALLENGE_CLOSE_GRACE_MS,
                );
            });
        });
        pendingRef.current = pending ?? null;

        try {
            recaptchaRef.current?.reset();
            const execution = recaptchaRef.current?.executeAsync();
            const result = execution
                ? await Promise.race([execution, interrupted])
                : null;

            if (typeof result === 'symbol') {
                if (result === CANCELED) {
                    recaptchaRef.current?.reset();
                    message.error('Verificação de segurança não concluída');
                }
                return null;
            }
            if (!result) {
                message.error(
                    'Falha na verificação de segurança, tente novamente',
                );
                return null;
            }
            return result;
        } catch {
            message.error('Falha na verificação de segurança, tente novamente');
            return null;
        } finally {
            cleanup();
            if (pendingRef.current === pending) pendingRef.current = null;
        }
    }, []);

    return { recaptchaRef, getRecaptchaToken, cancelRecaptcha };
};
